import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/auth";

/* ============================================================
   ReferralCard — a shareable-link block for the user's dashboard.

   Shows the user's personal referral link, how many people they've
   referred, and how many more until the next reward milestone
   (+10 free battles per 3 referrals). One-tap copy so users can
   post the link to socials without navigating a settings screen.

   Zero conversations required — the site does the marketing.
   ============================================================ */

export default function ReferralCard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/referrals/mine`)
      .then(r => setStats(r.data))
      .catch(() => setStats(null));
  }, [user]);

  if (!user || !stats) return null;

  const fullUrl = typeof window !== "undefined"
    ? `${window.location.origin}${stats.link_path}`
    : stats.link_path;

  const shareText = `I've been dropping tracks on Young Tongues — an AI rap battle + song-charts platform built by an Alberta farmer. Real writers only. Come pick a fight. ${fullUrl}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Young Tongues",
          text: shareText,
          url: fullUrl,
        });
      } catch (e) {}
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {}
    }
  };

  const progressPct = Math.min(100, ((3 - stats.remaining_to_next_reward) / 3) * 100);

  return (
    <div data-testid="referral-card"
         className="border-4 border-yt-yellow bg-gradient-to-br from-yt-surface via-black to-yt-surface shadow-brutal p-6 md:p-8 mb-8">
      <div className="font-mono text-[10px] tracking-[0.5em] text-yt-yellow mb-2">GROWTH PACT</div>
      <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight mb-4">
        BRING <span className="text-yt-yellow">3 WRITERS</span> · GET <span className="text-yt-yellow">10 FREE BATTLES</span>
      </h2>
      <p className="font-mono text-xs text-white/70 tracking-wide leading-relaxed mb-6">
        Share your link. Every friend who signs up under it gets you closer to your next reward.
        No spam. No talking. Just paste and post.
      </p>

      {/* Referral link + copy */}
      <div className="bg-black border-2 border-yt-yellow p-3 flex items-center gap-3 mb-4">
        <div data-testid="referral-link" className="flex-1 font-mono text-xs md:text-sm text-yt-yellow truncate">
          {fullUrl}
        </div>
        <button
          data-testid="referral-copy"
          onClick={copyLink}
          className="bg-yt-yellow text-black font-display text-xs md:text-sm tracking-widest px-4 py-2 hover:bg-white shrink-0"
        >
          {copied ? "✓ COPIED" : "COPY"}
        </button>
      </div>

      {/* Native share button (mobile-friendly) */}
      <button
        data-testid="referral-share"
        onClick={nativeShare}
        className="w-full md:w-auto bg-yt-yellow text-black font-display text-base md:text-lg tracking-widest px-6 py-3 shadow-brutal hover:bg-white mb-6"
      >
        SHARE MY LINK ▶
      </button>

      {/* Progress bar */}
      <div className="mt-6">
        <div className="flex items-center justify-between font-mono text-[10px] tracking-widest text-yt-muted mb-2">
          <span>{stats.count} REFERRALS</span>
          <span>{stats.remaining_to_next_reward} MORE FOR NEXT REWARD</span>
        </div>
        <div className="h-3 bg-yt-surface border-2 border-yt-border">
          <div className="h-full bg-yt-yellow transition-all"
               style={{ width: `${progressPct}%` }}
               data-testid="referral-progress" />
        </div>
        <div className="mt-2 font-mono text-[10px] text-yt-muted">
          {stats.reward_description}
        </div>
      </div>
    </div>
  );
}
